import { useMemo, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { constants } from '@blocktickets/shared-utils'

import { Confirm } from '..'
import { Spinner } from '../../LoadingContainer/Spinner'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { removeSeatsFromPackageOrder } from '../../../utilities/api'
import { formatCurrency } from '../../../utilities/helpers'

const sortTickets = (tickets = []) => {
  const safe = (v) => String(v ?? '')
  return [...tickets].sort((a, b) => {
    const aKey = `${safe(a.sectionNumber)}-${safe(a.rowNumber)}-${safe(a.seatNumber)}`
    const bKey = `${safe(b.sectionNumber)}-${safe(b.rowNumber)}-${safe(b.seatNumber)}`
    return aKey.localeCompare(bKey, undefined, { numeric: true })
  })
}

const formatPackageTicketSeat = (ticket) => {
  if (ticket?.GA) return `Sec ${ticket.sectionNumber} (GA)`
  return `Sec ${ticket.sectionNumber}, Row ${ticket.rowNumber}, Seat ${ticket.seatNumber}`
}

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100

export default function RemoveSeasonSeats({ order, onSuccess }) {
  const { closeModal, openModal } = useModal()
  const { ORDER_STATUS } = constants

  const [selectedIds, setSelectedIds] = useState(new Set())
  const [issueRefund, setIssueRefund] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: '' })

  const packageTickets = useMemo(
    () => sortTickets(order?.package_tickets || []),
    [order?.package_tickets]
  )

  const hasPaymentIntent =
    order?.paymentIntentId && String(order.paymentIntentId).trim() !== ''

  const canOfferRefund =
    hasPaymentIntent &&
    (order?.status === ORDER_STATUS.COMPLETE ||
      order?.status === ORDER_STATUS.PLAN)

  const selectedTickets = useMemo(
    () => packageTickets.filter((ticket) => selectedIds.has(ticket.id)),
    [packageTickets, selectedIds]
  )

  const selectedCount = selectedTickets.length
  const orderTotal = roundMoney(order?.total)
  const suggestedRefundAmount =
    packageTickets.length > 0 && selectedCount > 0
      ? roundMoney((orderTotal / packageTickets.length) * selectedCount)
      : 0

  const resolvedRefundAmount = roundMoney(
    refundAmount === '' ? suggestedRefundAmount : refundAmount
  )

  const toggleTicket = (ticketId) => {
    const next = new Set(selectedIds)
    if (next.has(ticketId)) next.delete(ticketId)
    else next.add(ticketId)
    setSelectedIds(next)
  }

  const handleRefundToggle = (checked) => {
    setIssueRefund(checked)
    if (checked && refundAmount === '') {
      setRefundAmount(String(suggestedRefundAmount || ''))
    }
  }

  const openConfirm = () => {
    if (!selectedCount) return

    const seatLabels = selectedTickets
      .map((ticket) => formatPackageTicketSeat(ticket))
      .join('; ')

    let description = `Remove ${selectedCount} seat${
      selectedCount === 1 ? '' : 's'
    } from this order and release them back to inventory? Seats: ${seatLabels}.`

    if (issueRefund && canOfferRefund) {
      description += ` A partial refund of ${formatCurrency(
        resolvedRefundAmount
      )} will be issued to the original payment method.`
    }

    openModal(
      Confirm,
      {
        title: 'Remove selected seats?',
        description,
        onConfirm: () => {
          closeModal()
          handleRemove()
        },
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  const handleRemove = async () => {
    try {
      setIsSaving(true)
      setAlert({ show: false, message: '' })

      const payload = {
        orderId: order?.id,
        fromPackageTicketIds: Array.from(selectedIds),
      }

      if (issueRefund && canOfferRefund) {
        payload.processRefund = true
        payload.refundAmount = resolvedRefundAmount
      }

      await removeSeatsFromPackageOrder(payload)

      if (typeof onSuccess === 'function') onSuccess()
      closeModal()
    } catch (err) {
      setAlert({
        show: true,
        message:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Unable to remove seats. Please try again.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal.Body className="d-flex-column">
      <div className="modal-body-heading">
        <div className="section-heading section-heading--secondary">
          <h1 className="mt-2 mb-0">Remove Seats</h1>
          <p className="section-header-desc mt-1 mb-0">
            Select seats to remove from this package order. Removed seats will
            be released back to inventory.
          </p>
        </div>
      </div>

      {alert.show && (
        <Alert variant="danger" className="mb-3">
          <p className="mb-0">{alert.message}</p>
        </Alert>
      )}

      <Stack gap={3}>
        <Stack gap={2}>
          <h3 className="small-bold mb-0">Current Seats</h3>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {packageTickets.map((ticket) => (
              <Form.Check
                key={ticket.id}
                id={`remove-seat-${ticket.id}`}
                checked={selectedIds.has(ticket.id)}
                onChange={() => toggleTicket(ticket.id)}
                className="mb-2"
                label={formatPackageTicketSeat(ticket)}
              />
            ))}
          </div>
        </Stack>

        <div className="small">
          Selected to remove: <strong>{selectedCount}</strong> seat
          {selectedCount === 1 ? '' : 's'}
        </div>

        {canOfferRefund && selectedCount > 0 && (
          <Stack gap={2}>
            <Form.Check
              id="remove-seats-issue-refund"
              type="switch"
              label="Issue partial refund"
              checked={issueRefund}
              onChange={(event) => handleRefundToggle(event.target.checked)}
            />
            {issueRefund && (
              <>
                <Form.Group controlId="remove-seats-refund-amount">
                  <Form.Label>Refund amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={refundAmount}
                    onChange={(event) => setRefundAmount(event.target.value)}
                    placeholder={String(suggestedRefundAmount || '0')}
                  />
                </Form.Group>
                <p className="small text-muted mb-0">
                  Suggested amount based on order total:{' '}
                  {formatCurrency(suggestedRefundAmount)}. The order total will
                  be reduced by the refund amount when a refund is issued.
                </p>
              </>
            )}
          </Stack>
        )}
      </Stack>

      <Stack
        direction="horizontal"
        className="btn-group-flex justify-content-end mt-4"
      >
        <Button variant="default" size="lg" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="lg"
          variant="outline-light"
          className="text-danger"
          disabled={!selectedCount || isSaving}
          onClick={openConfirm}
        >
          {isSaving ? <Spinner /> : 'Remove Seats'}
        </Button>
      </Stack>
    </Modal.Body>
  )
}
